import * as THREE from 'three';

/**
 * Smooth jersey fabric — soft micro-fiber only.
 * No hard pique / diamond grid (that read as plastic mesh under locker lights).
 */
export function createSoftClothTextures(size = 1024): {
  map: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
  normalMap: THREE.CanvasTexture;
  aoMap: THREE.CanvasTexture;
} {
  const albedo = document.createElement('canvas');
  albedo.width = albedo.height = size;
  const rough = document.createElement('canvas');
  rough.width = rough.height = size;
  const ao = document.createElement('canvas');
  ao.width = ao.height = size;
  const normal = document.createElement('canvas');
  normal.width = normal.height = size;

  const aCtx = albedo.getContext('2d')!;
  const rCtx = rough.getContext('2d')!;
  const aoCtx = ao.getContext('2d')!;
  const nCtx = normal.getContext('2d')!;

  const aImg = aCtx.createImageData(size, size);
  const rImg = rCtx.createImageData(size, size);
  const aoImg = aoCtx.createImageData(size, size);
  const nImg = nCtx.createImageData(size, size);
  const height = new Float32Array(size * size);

  const hash = (x: number, y: number) => {
    const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
    return n - Math.floor(n);
  };
  const smooth = (x: number, y: number) => {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    const fx = x - ix;
    const fy = y - iy;
    const u = fx * fx * (3 - 2 * fx);
    const v = fy * fy * (3 - 2 * fy);
    const a = hash(ix, iy);
    const b = hash(ix + 1, iy);
    const c = hash(ix, iy + 1);
    const d = hash(ix + 1, iy + 1);
    return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
  };
  const fbm = (x: number, y: number, oct = 5) => {
    let v = 0;
    let a = 0.5;
    let f = 1;
    for (let i = 0; i < oct; i++) {
      v += a * smooth(x * f, y * f);
      f *= 2;
      a *= 0.5;
    }
    return v;
  };

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const p = i * 4;

      // Clean kit white with readable micro-fiber (holds up on phone DPR)
      const fiber = fbm(x * 0.055, y * 0.055, 5);
      const fold = fbm(x * 0.012, y * 0.014, 3);
      const micro = fbm(x * 0.14, y * 0.13, 2);
      const shade = 0.965 + fiber * 0.028 + fold * 0.022 + micro * 0.012;

      const wr = Math.min(255, shade * 255);
      const wg = Math.min(255, shade * 254);
      const wb = Math.min(255, shade * 252);
      aImg.data[p] = wr;
      aImg.data[p + 1] = wg;
      aImg.data[p + 2] = wb;
      aImg.data[p + 3] = 255;

      const roughV = 0.72 + fiber * 0.1 + fold * 0.06;
      rImg.data[p] = 0;
      rImg.data[p + 1] = Math.min(255, roughV * 255);
      rImg.data[p + 2] = 0;
      rImg.data[p + 3] = 255;

      const aoV = 0.97 + fold * 0.025 + fiber * 0.012;
      aoImg.data[p] = aoV * 255;
      aoImg.data[p + 1] = aoV * 255;
      aoImg.data[p + 2] = aoV * 255;
      aoImg.data[p + 3] = 255;

      height[i] = fiber * 0.18 + fold * 0.22 + micro * 0.08;
    }
  }

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const p = i * 4;
      const hL = height[y * size + ((x - 1 + size) % size)];
      const hR = height[y * size + ((x + 1) % size)];
      const hD = height[((y - 1 + size) % size) * size + x];
      const hU = height[((y + 1) % size) * size + x];
      const nx = (hL - hR) * 0.7;
      const ny = (hD - hU) * 0.7;
      const nz = 1;
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
      nImg.data[p] = ((nx / len) * 0.5 + 0.5) * 255;
      nImg.data[p + 1] = ((ny / len) * 0.5 + 0.5) * 255;
      nImg.data[p + 2] = ((nz / len) * 0.5 + 0.5) * 255;
      nImg.data[p + 3] = 255;
    }
  }

  aCtx.putImageData(aImg, 0, 0);
  rCtx.putImageData(rImg, 0, 0);
  aoCtx.putImageData(aoImg, 0, 0);
  nCtx.putImageData(nImg, 0, 0);

  const toTex = (c: HTMLCanvasElement, color = true) => {
    const t = new THREE.CanvasTexture(c);
    if (color) t.colorSpace = THREE.SRGBColorSpace;
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(2.1, 2.1);
    t.anisotropy = 16;
    t.generateMipmaps = true;
    t.minFilter = THREE.LinearMipmapLinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.needsUpdate = true;
    return t;
  };

  return {
    map: toTex(albedo, true),
    roughnessMap: toTex(rough, false),
    normalMap: toTex(normal, false),
    aoMap: toTex(ao, false),
  };
}
